import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { noop } from 'lodash';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import messages from './messages';
import { Root, Title, Message, Container, IconWrap, AnnouncementMessage } from './styled';

export interface AnnouncementBannerDumbProps {
    isExpanded: boolean;
    isExpandable: boolean;
    expandBanner: () => void;
    shrinkBanner: () => void;
    onHeaderRef: (refNode: HTMLElement | null) => void;
    onMessageRef: (refNode: HTMLElement | null) => void;
    header?: string;
    messageHtml?: string;
    className?: string;
    /**
     * Useful when wanting to cap out the announcement to a particular width, but allow
     * the root container button to extend full width.
     */
    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
    // eslint-disable-next-line @typescript-eslint/ban-types
    InnerContainerComponent: React.ComponentType<{}>;
}

export class AnnouncementBannerDumb extends React.Component<AnnouncementBannerDumbProps & InjectedIntlProps> {
    handleClick = () => {
        const { isExpanded, shrinkBanner, expandBanner } = this.props;
        isExpanded ? shrinkBanner() : expandBanner();
    };

    renderChevrons() {
        const { isExpandable, isExpanded, intl } = this.props;

        if (isExpandable) {
            return isExpanded ? (
                <IconWrap>
                    <ChevronUpIcon label={intl.formatMessage(messages.shrink)} />
                </IconWrap>
            ) : (
                <IconWrap>
                    <ChevronDownIcon label={intl.formatMessage(messages.expand)} />
                </IconWrap>
            );
        }

        return null;
    }

    /**
     * This will stop propagation for any anchor element clicked or key pressed.
     * If we see a need for this elsewhere - this could be a good generic component with minimal lines
     * of code.
     */
    stopPropagationForLinks = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'A') {
            e.stopPropagation();
        }
    };

    render() {
        const {
            isExpanded,
            isExpandable,
            header,
            messageHtml,
            onMessageRef,
            onHeaderRef,
            className,
            InnerContainerComponent,
        } = this.props;
        const handleClick = isExpandable ? this.handleClick : noop;

        if (!!header || !!messageHtml) {
            return (
                <Root onClick={handleClick} className={className} disabled={!isExpandable}>
                    <InnerContainerComponent>
                        <Container>
                            <AnnouncementMessage // Set aria alert so screen readers read this out ASAP.
                                role="alert"
                                // We want this to be the first thing read out (assumed high priority).
                                aria-live="assertive"
                                onClick={this.stopPropagationForLinks}
                                onKeyDown={this.stopPropagationForLinks}
                            >
                                {header && (
                                    <Title innerRef={onHeaderRef} isExpanded={isExpanded}>
                                        {header}
                                    </Title>
                                )}
                                {messageHtml && (
                                    <Message
                                        innerRef={onMessageRef}
                                        isExpanded={isExpanded}
                                        dangerouslySetInnerHTML={{ __html: messageHtml }}
                                    />
                                )}
                            </AnnouncementMessage>

                            {/* only render chevon arrows if the banner is expandable */}
                            {this.renderChevrons()}
                        </Container>
                    </InnerContainerComponent>
                </Root>
            );
        }

        return null;
    }
}

export default injectIntl(AnnouncementBannerDumb);
